import { DataGlueGraph, NodeInstance, FunctionNodeInstance, FunctionDefinition } from '@glueapp/graph-types';
import { ValidationRule, ValidationRuleResult, ValidationError } from '../ValidationRule';

export const CircularFunctionReferenceRule: ValidationRule = (graph: DataGlueGraph): ValidationRuleResult => {
    const errors: ValidationError[] = [];

    if (!graph.functions || graph.functions.length === 0) {
        return { valid: true, errors: [] };
    }

    // Build a map of function IDs to their definitions
    const functionMap = new Map<string, FunctionDefinition>();
    for (const func of graph.functions) {
        functionMap.set(func.id, func);
    }

    // Check each function for circular references
    for (const func of graph.functions) {
        const visited = new Set<string>();
        const recursionStack = new Set<string>();
        const cycle = detectCircularFunctionReference(func.id, functionMap, visited, recursionStack);

        if (cycle.length > 0) {
            errors.push({
                code: 'CIRCULAR_FUNCTION_REFERENCE',
                message: `Circular function reference detected: ${cycle.join(' → ')}`,
                severity: 'error',
                location: {
                    functionId: func.id,
                    cycle,
                },
            });
        }
    }

    return {
        valid: errors.length === 0,
        errors,
    };
};

function detectCircularFunctionReference(
    functionId: string,
    functionMap: Map<string, FunctionDefinition>,
    visited: Set<string>,
    recursionStack: Set<string>,
    path: string[] = [],
): string[] {
    // If we've already fully processed this function, no cycle from here
    if (visited.has(functionId) && !recursionStack.has(functionId)) {
        return [];
    }

    // If this function is in the recursion stack, we found a cycle
    if (recursionStack.has(functionId)) {
        const cycleStart = path.indexOf(functionId);
        return [...path.slice(cycleStart), functionId];
    }

    visited.add(functionId);
    recursionStack.add(functionId);
    path.push(functionId);

    const functionDef = functionMap.get(functionId);
    if (!functionDef) {
        recursionStack.delete(functionId);
        return [];
    }

    // Check all node instances in this function
    for (const node of functionDef.nodeInstances) {
        if (isFunctionNodeInstance(node)) {
            // Check if this node references a function
            const referencedFunctionId = node.functionId;

            // Recursively check the referenced function
            const cycle = detectCircularFunctionReference(referencedFunctionId, functionMap, visited, recursionStack, [...path]);

            if (cycle.length > 0) {
                return cycle;
            }
        }
    }

    // Remove from recursion stack after processing
    recursionStack.delete(functionId);
    return [];
}

function isFunctionNodeInstance(node: NodeInstance): node is FunctionNodeInstance {
    return 'functionId' in node && typeof (node as any).functionId === 'string';
}
